import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { ParkingLotItemDetail } from '@jabberr/core';
import { api, errorMessage } from '../lib/api.js';

/**
 * "What should happen with this?" (design I-End): Add to parking lot (Jabberr drafts a note from this conversation;
 * nothing gets built) or Draft requirements (the existing fixed turn). Shown after Jabberr's reply when the person
 * wraps up, and when they pick Add to parking lot from the Write... menu (autoPark).
 */
export function ParkEndCard({
  conversationId,
  autoPark = false,
  canDraft,
  onDraftRequirements,
  onParked,
  onDismiss,
}: {
  conversationId: string;
  autoPark?: boolean;
  /** Draft requirements is internal work: not offered to guests. */
  canDraft: boolean;
  onDraftRequirements: () => void;
  onParked?: () => void;
  onDismiss: () => void;
}) {
  const [state, setState] = useState<'idle' | 'parking' | 'parked' | 'error'>('idle');
  const [item, setItem] = useState<ParkingLotItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  const park = async (): Promise<void> => {
    if (state === 'parking') return;
    setState('parking');
    setError(null);
    try {
      const r = await api.parkingLot.fromConversation(conversationId, crypto.randomUUID());
      setItem(r.item);
      setState('parked');
      onParked?.();
    } catch (err) {
      setError(errorMessage(err));
      setState('error');
    }
  };

  useEffect(() => {
    if (autoPark && !started.current) {
      started.current = true;
      void park();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPark]);

  if (state === 'parked' && item) {
    return (
      <div className="end-card done" role="status">
        <span className="end-card-title">Added to the parking lot: {item.title}</span>
        <span className="end-card-hint">
          {item.summary ? `${item.summary} ` : ''}
          <Link to={`/parking-lot/${item.id}`}>Open the note</Link>
        </span>
        <button type="button" className="end-card-close" aria-label="Dismiss" onClick={onDismiss}>
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="end-card" role="group" aria-label="What should happen with this?">
      <span className="end-card-title">What should happen with this?</span>
      <div className={`end-card-choices${canDraft ? '' : ' single'}`}>
        <button type="button" className="end-choice park" onClick={() => void park()} disabled={state === 'parking'}>
          <span className="end-choice-title">{state === 'parking' ? 'Jabberr is writing the note…' : 'Add to parking lot'}</span>
          <span className="end-choice-sub">Save it as a note with a short write-up. Nothing gets built.</span>
        </button>
        {canDraft ? (
          <button type="button" className="end-choice draft" onClick={onDraftRequirements} disabled={state === 'parking'}>
            <span className="end-choice-title">Draft requirements</span>
            <span className="end-choice-sub">Write it up properly. Requirements go to Loop for John's OK before anything is built.</span>
          </button>
        ) : null}
      </div>
      {error ? <span className="end-card-error" role="alert">{error}</span> : null}
      <span className="end-card-hint">Or keep talking. You can do either later from the parking lot.</span>
      <button type="button" className="end-card-close" aria-label="Not now" onClick={onDismiss}>
        ×
      </button>
    </div>
  );
}
